import React, { useState } from "react";
import { Mail, MapPin, Phone, Send, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Button } from "./ui/button";
import { Label } from "./ui/label";
import { submitContact } from "../lib/api";
import { BRAND } from "../lib/brand";

export const ContactSection = () => {
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [submitting, setSubmitting] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill in name, email and message.");
      return;
    }
    setSubmitting(true);
    try {
      await submitContact(form);
      toast.success("Message sent — we'll be in touch shortly.");
      setForm({ name: "", email: "", subject: "", message: "" });
    } catch (err) {
      toast.error("Couldn't send message. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="relative bg-slate-50 py-24 lg:py-32"
    >
      <div className="mx-auto max-w-7xl px-6 lg:px-8">
        <div className="grid grid-cols-1 gap-12 lg:grid-cols-12 lg:gap-16">
          {/* Left: copy + details */}
          <div className="lg:col-span-5">
            <div className="mb-4 inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-3 py-1.5">
              <span className="h-1.5 w-1.5 rounded-full bg-[#00A3E0]" />
              <span className="text-[11px] font-semibold uppercase tracking-[0.22em] text-slate-600">
                Contact Us
              </span>
            </div>
            <h2 className="font-display text-4xl font-extrabold leading-[1.05] tracking-tight text-slate-900 sm:text-5xl">
              Start a conversation.
            </h2>
            <p className="mt-5 text-base leading-relaxed text-slate-600">
              Whether you&apos;re shaping an early-stage concept or restructuring a
              stalled program, our team will respond within one business day.
            </p>

            <div className="mt-10 space-y-5">
              <div className="flex items-start gap-4">
                <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-[#00A3E0]/10 text-[#00A3E0]">
                  <Mail className="h-5 w-5" />
                </div>
                <div>
                  <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
                    Email
                  </p>
                  <a
                    href={`mailto:${BRAND.email}`}
                    data-testid="contact-email-link"
                    className="text-base font-semibold text-slate-900 hover:text-[#00A3E0]"
                  >
                    {BRAND.email}
                  </a>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-[#00A3E0]/10 text-[#00A3E0]">
                  <Phone className="h-5 w-5" />
                </div>
                <div>
                  <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
                    Phone
                  </p>
                  <p className="text-base font-semibold text-slate-900">
                    By appointment
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-[#00A3E0]/10 text-[#00A3E0]">
                  <MapPin className="h-5 w-5" />
                </div>
                <div>
                  <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
                    Headquarters
                  </p>
                  <p className="text-base font-semibold text-slate-900">
                    United States
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Right: form */}
          <div className="lg:col-span-7">
            <form
              onSubmit={onSubmit}
              data-testid="contact-form"
              className="rounded-2xl border border-slate-200 bg-white p-7 shadow-sm sm:p-9"
            >
              <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                <div>
                  <Label htmlFor="c-name">Full name</Label>
                  <Input
                    id="c-name"
                    data-testid="contact-name-input"
                    value={form.name}
                    onChange={update("name")}
                    placeholder="Jane Doe"
                    className="mt-2"
                  />
                </div>
                <div>
                  <Label htmlFor="c-email">Email</Label>
                  <Input
                    id="c-email"
                    type="email"
                    data-testid="contact-email-input"
                    value={form.email}
                    onChange={update("email")}
                    placeholder="jane@company.com"
                    className="mt-2"
                  />
                </div>
              </div>
              <div className="mt-5">
                <Label htmlFor="c-subject">Subject</Label>
                <Input
                  id="c-subject"
                  data-testid="contact-subject-input"
                  value={form.subject}
                  onChange={update("subject")}
                  placeholder="Brief subject of your inquiry"
                  className="mt-2"
                />
              </div>
              <div className="mt-5">
                <Label htmlFor="c-message">Message</Label>
                <Textarea
                  id="c-message"
                  data-testid="contact-message-input"
                  value={form.message}
                  onChange={update("message")}
                  placeholder="Tell us about your project, ambition or question…"
                  className="mt-2 min-h-[140px]"
                />
              </div>
              <div className="mt-7 flex items-center justify-between gap-4">
                <p className="text-xs text-slate-500">
                  We respond to qualified inquiries within 1 business day.
                </p>
                <Button
                  type="submit"
                  disabled={submitting}
                  data-testid="contact-submit-btn"
                  className="rounded-full bg-[#00A3E0] px-6 text-white hover:bg-[#0089bd]"
                >
                  {submitting ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Sending…
                    </>
                  ) : (
                    <>
                      Send message <Send className="ml-2 h-4 w-4" />
                    </>
                  )}
                </Button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;
